import {
  PERIOD_OPTIONS,
  PeriodOption,
  PeriodPreset,
  periodPhrase,
  periodRange,
  toLocalIsoDate
} from './period-range';

/** What a period filter offers: the presets, or days of the user's own ("Personalizado"). */
export type PeriodChoice = PeriodPreset | 'CUSTOM';

export interface PeriodChoiceOption {
  readonly value: PeriodChoice;
  readonly label: string;
}

/**
 * The period a list is narrowed to. `from` and `to` are local calendar days as YYYY-MM-DD, both inclusive,
 * read only under CUSTOM; either may be `null`, leaving that side open.
 */
export interface PeriodSelection {
  readonly choice: PeriodChoice;
  readonly from: string | null;
  readonly to: string | null;
}

/** Calendar days as YYYY-MM-DD, both ends inclusive; a `null` end leaves that side open. */
export interface DateBounds {
  readonly from: string | null;
  readonly to: string | null;
}

/** The days each custom date can take, as the date picker reads them; `null` leaves that side open. */
export interface CustomPeriodLimits {
  readonly fromMax: Date | null;
  readonly toMin: Date | null;
  readonly toMax: Date | null;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const choiceOf = ({ value, label }: PeriodOption): PeriodChoiceOption => ({ value, label });

/** The whole period first, as "Todos" leads the status tabs; the user's own days last. */
export const PERIOD_CHOICES: readonly PeriodChoiceOption[] = [
  ...PERIOD_OPTIONS.filter(option => option.value === 'ALL_TIME').map(choiceOf),
  ...PERIOD_OPTIONS.filter(option => option.value !== 'ALL_TIME').map(choiceOf),
  { value: 'CUSTOM', label: 'Personalizado' }
];

export function presetSelection(preset: PeriodPreset): PeriodSelection {
  return { choice: preset, from: null, to: null };
}

export function customSelection(from: string | null, to: string | null): PeriodSelection {
  return { choice: 'CUSTOM', from, to };
}

/**
 * The selection after another option is picked. "Personalizado" starts from the days on screen, so the list
 * does not change until a date does; a preset drops the custom days.
 */
export function choosePeriod(current: PeriodSelection, choice: PeriodChoice, today: Date): PeriodSelection {
  if (choice === current.choice) {
    return current;
  }

  if (choice === 'CUSTOM') {
    const range = periodSelectionRange(current, today);

    return customSelection(range?.from ?? null, range?.to ?? null);
  }

  return presetSelection(choice);
}

/** A new first day. An end the new day would pass is cleared: the day just picked wins. */
export function withCustomFrom(current: PeriodSelection, from: string | null): PeriodSelection {
  return customSelection(from, from !== null && current.to !== null && from > current.to ? null : current.to);
}

/** A new last day. A start the new day would come before is cleared: the day just picked wins. */
export function withCustomTo(current: PeriodSelection, to: string | null): PeriodSelection {
  return customSelection(to !== null && current.from !== null && to < current.from ? null : current.from, to);
}

/**
 * The calendar days a selection covers, for the API. `null` means unbounded. A reversed custom range comes
 * back in order, so a request never carries a start after its end.
 */
export function periodSelectionRange(selection: PeriodSelection, today: Date): DateBounds | null {
  return selection.choice === 'CUSTOM' ? customBounds(selection) : periodRange(selection.choice, today);
}

/** Whether the selection narrows the list at all. */
export function hasPeriodBounds(selection: PeriodSelection): boolean {
  return selection.choice === 'CUSTOM' ? customBounds(selection) !== null : selection.choice !== 'ALL_TIME';
}

/** Finishes a sentence about the list: "Nenhum pedido pendente entre 01/09/2026 e 13/09/2026". */
export function periodSelectionPhrase(selection: PeriodSelection): string {
  if (selection.choice !== 'CUSTOM') {
    return periodPhrase(selection.choice);
  }

  const bounds = customBounds(selection);
  const from = bounds?.from ?? null;
  const to = bounds?.to ?? null;

  if (from === null) {
    return to === null ? '' : `até ${formatIsoDate(to)}`;
  }
  if (to === null) {
    return `desde ${formatIsoDate(from)}`;
  }

  return from === to ? `em ${formatIsoDate(from)}` : `entre ${formatIsoDate(from)} e ${formatIsoDate(to)}`;
}

export function samePeriodSelection(a: PeriodSelection, b: PeriodSelection): boolean {
  return a.choice === b.choice && a.from === b.from && a.to === b.to;
}

/** Whether two ranges cover the same days, so a computed holding them can skip an update that changes nothing. */
export function sameDateBounds(a: DateBounds | null, b: DateBounds | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }

  return a.from === b.from && a.to === b.to;
}

/** DD/MM/YYYY of a YYYY-MM-DD day, read off the string so no time zone gets in. */
export function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.split('-');

  return `${day}/${month}/${year}`;
}

/** The local midnight of a YYYY-MM-DD day, as a date picker holds it; `null` for anything else. */
export function parseLocalIsoDate(iso: string | null): Date | null {
  const match = iso === null ? null : ISO_DATE.exec(iso);

  if (match === null) {
    return null;
  }

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** The local day a date picker holds, as YYYY-MM-DD; `null` while its field holds no valid date. */
export function localIsoDateOrNull(value: unknown): string | null {
  return value instanceof Date && !Number.isNaN(value.getTime()) ? toLocalIsoDate(value) : null;
}

/**
 * How far each custom date can go: the first day up to the last one (or `latest`), the last day from the first
 * one up to `latest`. Linked like this, a reversed range cannot be picked or typed.
 */
export function customPeriodLimits(from: Date | null, to: Date | null, latest: Date | null): CustomPeriodLimits {
  return { fromMax: earlier(to, latest), toMin: from, toMax: latest };
}

function customBounds({ from, to }: PeriodSelection): DateBounds | null {
  if (from === null && to === null) {
    return null;
  }

  // YYYY-MM-DD sorts as the calendar does.
  return from !== null && to !== null && from > to ? { from: to, to: from } : { from, to };
}

function earlier(a: Date | null, b: Date | null): Date | null {
  if (a === null || b === null) {
    return a ?? b;
  }

  return a.getTime() <= b.getTime() ? a : b;
}
